import { axiosConfig } from "@/services/axiosConfig";
import type { SubmissionStatus } from "@/projects/lib/store";

const DASHBOARD_PATH = "/rajnagar_gyanmahostav/dashboard/";

export type DashboardPeriod =
  | "last_7_days"
  | "last_30_days"
  | "all_time";

export type PeriodFilter = {
  key: DashboardPeriod | string;
  label: string;
  selected: boolean;
};

export type LiveInsight = {
  key: string;
  label: string;
  value: number;
  trend_percent: number;
  trend_direction: "up" | "down" | string;
  sparkline: number[];
};

export type TrendPoint = {
  date: string;
  submissions: number;
  verified: number;
};

export type StatusDistributionItem = {
  status: string;
  label: string;
  count: number;
};

export type TeacherPerformanceItem = {
  teacher_id: string;
  user_id: string;
  name: string;
  full_name?: string;
  ready_for_reward: number;
  not_proper: number;
  pending: number;
};

export type TopVerifierItem = {
  teacher_id: string;
  user_id: string;
  name: string;
  full_name?: string;
  processed: number;
};

export type RecentSubmissionItem = {
  id: number | string;
  reference: string;
  title: string;
  student_name: string;
  student_id: string;
  submitted_at: string;
  status: string;
  status_display?: string;
};

export type AdminDashboardData = {
  period: DashboardPeriod | string;
  period_label: string;
  period_filters: PeriodFilter[];
  live_insights: LiveInsight[];
  submission_trend: {
    title: string;
    subtitle: string;
    series: TrendPoint[];
  };
  reward_rate: {
    title: string;
    subtitle: string;
    percent: number;
    of_submissions: number;
    verification_rate_percent: number;
  };
  status_distribution: {
    title: string;
    subtitle: string;
    total: number;
    items: StatusDistributionItem[];
  };
  teacher_performance: {
    title: string;
    subtitle: string;
    items: TeacherPerformanceItem[];
  };
  top_verifiers: {
    title: string;
    subtitle: string;
    items: TopVerifierItem[];
  };
  momentum: {
    title: string;
    subtitle: string;
    series: TrendPoint[];
  };
  recent_submissions: {
    title: string;
    subtitle: string;
    items: RecentSubmissionItem[];
  };
};

type DashboardPayload =
  | AdminDashboardData
  | { success?: boolean; message?: string; data?: AdminDashboardData };

function unwrapDashboard(payload: DashboardPayload): AdminDashboardData {
  if (payload && typeof payload === "object" && "data" in payload && payload.data) {
    return payload.data;
  }
  return payload as AdminDashboardData;
}

export function formatDashboardDate(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) {
    return isoDate;
  }
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export function normalizeDashboardStatus(raw?: string): SubmissionStatus {
  if (!raw) {
    return "Pending";
  }
  const v = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (["pending", "submitted", "new"].includes(v)) {
    return "Pending";
  }
  if (["assigned", "assigned_for_verification", "under_verification"].includes(v)) {
    return "Assigned for Verification";
  }
  if (["not_proper", "rejected"].includes(v)) {
    return "Not Proper";
  }
  if (["ready_for_reward", "approved", "verified"].includes(v)) {
    return "Ready for Reward";
  }
  if (
    raw === "Pending" ||
    raw === "Assigned for Verification" ||
    raw === "Not Proper" ||
    raw === "Ready for Reward"
  ) {
    return raw;
  }
  return "Pending";
}

export async function fetchAdminDashboard(
  period: DashboardPeriod = "last_30_days"
): Promise<AdminDashboardData> {
  const { data } = await axiosConfig.get<DashboardPayload>(DASHBOARD_PATH, {
    params: { period },
    headers: { Accept: "application/json" },
  });
  return unwrapDashboard(data);
}

export function getAdminDashboardErrorMessage(
  error: unknown,
  fallback: string
): string {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  for (const key of ["message", "detail", "error"]) {
    const v = data[key];
    if (typeof v === "string" && v.trim()) {
      return v;
    }
  }
  return fallback;
}

export function insightValue(
  insights: LiveInsight[],
  key: string,
  fallback = 0
): number {
  return insights.find(i => i.key === key)?.value ?? fallback;
}
